import { useEffect, useRef } from 'preact/hooks'
import { scrollHistoryManager } from 'helpers/scroll-history'

/**
 * Restores the grid to the product the user clicked through to.
 *
 * A card is only an approximation of its final size when it first hits the DOM — Klaviyo
 * ratings, badges and promolines all resolve asynchronously, and anything above the grid is
 * still settling too. A single scroll therefore lands short by however much the page grows
 * afterwards, so the anchor is re-aligned until it holds its position. Settling is measured
 * against the achieved scroll rather than the requested one, because a target below the
 * bottom of a page that is still filling out gets clamped, and treating that as arrival
 * strands the user at the bottom.
 */

const SETTLE_BUDGET_MS = 3000
const STABLE_FRAMES = 3
const GROWTH_PATIENCE_FRAMES = 40
const TAKEOVER_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown']

export function useScrollCache(deps = []) {
  const restored = useRef(false)
  const teardown = useRef(null)

  useEffect(() => () => teardown.current?.(), [])

  useEffect(() => {
    if (restored.current) return

    const handle = scrollHistoryManager.productHandle
    if (!handle) return

    const selector = `result__${handle}`
    const { savedAnchorTop, savedScrollPosition } = scrollHistoryManager

    if (!document.getElementById(selector) &&
      (savedScrollPosition === undefined || !document.querySelector('[id^="result__"]'))) return

    restored.current = true

    let frame = null
    let stableFrames = 0
    let staleHeightFrames = 0
    let previousLimit = null
    const deadline = performance.now() + SETTLE_BUDGET_MS

    const stop = () => {
      if (frame !== null) cancelAnimationFrame(frame)
      frame = null
      TAKEOVER_EVENTS.forEach((event) => window.removeEventListener(event, finish))
    }

    function finish() {
      stop()
      scrollHistoryManager.clearScrollHistory()
    }

    const resolveTarget = () => {
      const anchor = document.getElementById(selector)
      if (!anchor) return savedScrollPosition

      const rect = anchor.getBoundingClientRect()
      const offset = savedAnchorTop ?? rect.height / 2
      return Math.max(0, Math.round(window.scrollY + rect.top - offset))
    }

    const align = () => {
      const target = resolveTarget()
      const limit = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
      const reachable = Math.min(target, limit)

      if (Math.abs(window.scrollY - reachable) > 1) {
        window.scrollTo({ left: 0, top: reachable, behavior: 'instant' })
      }

      const arrived = target <= limit + 1 && Math.abs(window.scrollY - target) <= 1
      stableFrames = arrived ? stableFrames + 1 : 0
      staleHeightFrames = limit === previousLimit ? staleHeightFrames + 1 : 0
      previousLimit = limit

      const outOfReach = !arrived && staleHeightFrames >= GROWTH_PATIENCE_FRAMES

      if (stableFrames >= STABLE_FRAMES || outOfReach || performance.now() >= deadline) {
        finish()
        return
      }

      frame = requestAnimationFrame(align)
    }

    TAKEOVER_EVENTS.forEach((event) => window.addEventListener(event, finish, { passive: true }))
    teardown.current = stop

    align()
  }, deps)
}
